import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { SmartLink, SponsoredLabel } from '@/components/shop/storefront/common';
import { rupees } from '@/lib/shop';
import { usePrefersReducedMotion, useSectionTracking } from '@/lib/shopHome';

/*
 * Banner rendering shared by the hero carousel and the promo banners. A slide
 * is either uploaded art (desktop + phone image) or a "designed" banner the
 * admin builds from a gradient, a headline and a product photo.
 */

/** Per-placement sizing: the box keeps a fixed ratio so nothing shifts as art loads. */
const SIZES = {
  hero: {
    box: 'aspect-[4/3] sm:aspect-[2/1] lg:aspect-[16/5]',
    pad: 'px-5 pb-9 pt-5 sm:px-10 sm:pb-10 sm:pt-8 md:px-20 lg:px-24',
    headline: 'text-[1.45rem] sm:text-[2.1rem] lg:text-[2.75rem]',
    sub: 'text-[13px] sm:text-base',
    frame: 'h-[66%] sm:h-[80%] lg:h-[84%]',
  },
  wide: {
    box: 'aspect-[2/1] sm:aspect-[3/1] lg:aspect-[4/1]',
    pad: 'px-5 py-4 sm:px-10 sm:py-6 lg:px-14',
    headline: 'text-[1.3rem] sm:text-[1.8rem] lg:text-[2.2rem]',
    sub: 'text-[13px] sm:text-[15px]',
    frame: 'h-[76%] sm:h-[84%]',
  },
  split: {
    box: 'aspect-[2/1] lg:aspect-[12/5]',
    pad: 'px-5 py-4 sm:px-6 sm:py-5 lg:px-8',
    headline: 'text-[1.3rem] sm:text-[1.5rem] lg:text-[1.9rem]',
    sub: 'text-[13px] sm:text-sm',
    frame: 'h-[76%] sm:h-[82%]',
  },
};

const DesignedSlide = ({ slide, size, priority }) => {
  const d = slide.design;
  const light = d.textTone !== 'dark';
  const s = SIZES[size];
  const [from, to] = d.bg?.length ? d.bg : ['#0f3d91', '#3b82f6'];
  const badge = d.maxDiscount >= 5 ? `Up to ${d.maxDiscount}% off` : null;
  return (
    <div className="absolute inset-0 overflow-hidden" style={{ background: `linear-gradient(118deg, ${from} 0%, ${to} 100%)` }}>
      {/* Soft light shapes for depth; the scrim keeps light text readable on bright gradients. */}
      <span aria-hidden="true" className="pointer-events-none absolute -right-[12%] -top-[40%] h-[120%] w-[55%] rounded-full bg-white/[0.12] blur-2xl" />
      <span aria-hidden="true" className="pointer-events-none absolute -bottom-[45%] left-[30%] h-[90%] w-[40%] rounded-full bg-white/[0.08] blur-2xl" />
      {light && (
        <span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,.28) 0%, rgba(0,0,0,.08) 55%, transparent 80%)' }} />
      )}
      <div className={`relative flex h-full items-center gap-3 sm:gap-6 ${s.pad}`}>
        <div className={`flex min-w-0 flex-1 flex-col items-start ${light ? 'text-white' : 'text-ink-900'}`}>
          {d.eyebrow && (
            <span
              className={`mb-2 inline-flex max-w-full truncate rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] sm:mb-3 sm:text-[11px] ${
                light ? 'bg-white/20 text-white' : 'bg-ink-900/10 text-ink-900'
              }`}
            >
              {d.eyebrow}
            </span>
          )}
          <p
            className={`line-clamp-3 font-display font-extrabold leading-[1.05] tracking-tight ${s.headline}`}
            style={light ? { textShadow: '0 1px 12px rgba(0,0,0,.18)' } : undefined}
          >
            {d.headline}
          </p>
          {d.subtext && (
            <p className={`mt-1.5 line-clamp-2 max-w-md sm:mt-2.5 ${s.sub} ${light ? 'text-white/90' : 'text-ink-700'}`}>{d.subtext}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 sm:mt-5">
            <span
              className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-4 text-[13px] font-semibold shadow-card transition-transform group-hover:translate-x-0.5 sm:min-h-[44px] sm:px-5 sm:text-sm ${
                light ? 'bg-white text-ink-900' : 'bg-ink-900 text-white'
              }`}
            >
              {d.cta || 'Shop now'}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
            {d.fromPrice > 0 && (
              <span className={`text-xs font-medium sm:text-sm ${light ? 'text-white/90' : 'text-ink-700'}`}>
                From <span className="tabular font-bold">{rupees(d.fromPrice)}</span>
              </span>
            )}
          </div>
        </div>
        {d.image && (
          <div className={`relative aspect-[4/5] shrink-0 ${s.frame}`}>
            <div className="h-full w-full overflow-hidden rounded-2xl bg-white/20 shadow-[0_18px_40px_-12px_rgba(0,0,0,.45)] ring-[3px] ring-white/40 sm:rounded-3xl sm:ring-4">
              <img
                src={d.image}
                alt=""
                draggable={false}
                loading={priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : 'auto'}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
            </div>
            {badge && (
              <span className="absolute -left-3 -top-2 flex h-[3.4rem] w-[3.4rem] rotate-[-10deg] flex-col items-center justify-center rounded-full bg-amber-300 text-center font-display text-[10px] font-extrabold uppercase leading-[1.05] text-ink-900 shadow-cardHover sm:-left-5 sm:-top-3 sm:h-[4.6rem] sm:w-[4.6rem] sm:text-[13px]">
                <span className="text-[8px] sm:text-[10px]">Up to</span>
                <span className="text-[15px] sm:text-xl">{d.maxDiscount}%</span>
                <span className="text-[8px] sm:text-[10px]">off</span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ImageSlide = ({ slide, priority }) => (
  <picture className="absolute inset-0 block bg-surface-sunken">
    {slide.mobileImage && slide.mobileImage !== slide.image && <source media="(max-width: 639px)" srcSet={slide.mobileImage} />}
    <img
      src={slide.image}
      alt={slide.alt || ''}
      draggable={false}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      className="h-full w-full object-cover"
    />
  </picture>
);

/** One banner, as a link. `size`: hero | wide | split. */
export const BannerSlide = ({ slide, size = 'wide', priority = false, rounded = 'rounded-2xl sm:rounded-3xl', className = '' }) => (
  <SmartLink
    link={slide.link}
    data-item-key={slide.key}
    aria-label={slide.alt || slide.design?.headline}
    className={`lk-focus group relative block w-full overflow-hidden ${rounded} ${SIZES[size].box} ${className}`}
  >
    {slide.design ? <DesignedSlide slide={slide} size={size} priority={priority} /> : <ImageSlide slide={slide} priority={priority} />}
    {slide.sponsored && <SponsoredLabel name={slide.sponsorName} tone="overlay" className="absolute bottom-2.5 right-2.5 sm:bottom-4 sm:right-4" />}
  </SmartLink>
);

/* ------------------------------------------------------------ hero */

export const HeroCarousel = ({ section }) => {
  const slides = section.data?.slides || [];
  const n = slides.length;
  const seconds = Math.min(15, Math.max(3, Number(section.data?.autoplaySeconds) || 5));
  const reduced = usePrefersReducedMotion();
  const track = useSectionTracking(section.id, [slides]);

  const [index, setIndex] = useState(0);
  const [drag, setDrag] = useState(0);
  const [playing, setPlaying] = useState(!reduced);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.visibilityState === 'hidden');
  const gesture = useRef(null);
  const swallowClick = useRef(false);

  useEffect(() => setPlaying(!reduced), [reduced]);
  useEffect(() => {
    const on = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);

  const go = useCallback((k) => setIndex(((k % n) + n) % n), [n]);
  const running = n > 1 && playing && !hovered && !focused && !hidden && drag === 0;

  // Autoplay is driven by the active dot's progress animation (see onAnimationEnd),
  // so pausing freezes the bar and the timer together. This timer is the fallback
  // for when animations are off (reduced motion with play pressed).
  useEffect(() => {
    if (!running || !reduced) return undefined;
    const t = setTimeout(() => go(index + 1), seconds * 1000);
    return () => clearTimeout(t);
  }, [running, reduced, index, seconds, go]);

  if (!n) return null;

  const onPointerDown = (e) => {
    if (n < 2 || (e.pointerType === 'mouse' && e.button !== 0)) return;
    gesture.current = { x: e.clientX, y: e.clientY, id: e.pointerId, axis: null, width: e.currentTarget.clientWidth };
  };
  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      g.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (g.axis === 'x') e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    if (g.axis === 'x') setDrag(dx);
  };
  const endGesture = (e) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g || g.axis !== 'x') return;
    const dx = e.clientX - g.x;
    const min = Math.min(60, g.width * 0.15);
    if (dx <= -min) go(index + 1);
    else if (dx >= min) go(index - 1);
    swallowClick.current = Math.abs(dx) > 6;
    setTimeout(() => {
      swallowClick.current = false;
    }, 0);
    setDrag(0);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(index - 1);
    }
  };

  const arrow =
    'lk-focus absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-ink-900 shadow-cardHover backdrop-blur transition hover:scale-105 hover:bg-white focus-visible:opacity-100 md:flex md:opacity-0 md:group-hover/hero:opacity-100';

  return (
    <section className="lk-container px-0 sm:px-6 sm:pt-4 lg:px-8 lg:pt-5" {...track}>
      <div
        className="group/hero relative overflow-hidden bg-surface-sunken sm:rounded-3xl sm:shadow-card"
        role="region"
        aria-roledescription="carousel"
        aria-label={section.title || 'Featured offers'}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
        }}
      >
        <div
          className="flex select-none"
          style={{
            touchAction: 'pan-y',
            transform: `translate3d(calc(${-index * 100}% + ${drag}px), 0, 0)`,
            transition: drag || reduced ? 'none' : 'transform 560ms cubic-bezier(.22,.8,.24,1)',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          onClickCapture={(e) => {
            if (swallowClick.current) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          aria-live={running ? 'off' : 'polite'}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.key || i}
              className="w-full shrink-0"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${n}`}
              aria-hidden={i !== index}
              inert={i !== index ? true : undefined}
            >
              <BannerSlide slide={slide} size="hero" priority={i === 0} rounded="rounded-none" />
            </div>
          ))}
        </div>

        {n > 1 && (
          <>
            <button type="button" aria-label="Previous slide" onClick={() => go(index - 1)} className={`${arrow} left-4`}>
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" aria-label="Next slide" onClick={() => go(index + 1)} className={`${arrow} right-4`}>
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute inset-x-0 bottom-2.5 z-10 flex items-center justify-center gap-1 sm:bottom-4">
              <div className="flex items-center gap-0.5 rounded-full bg-black/25 px-1.5 py-0.5 backdrop-blur-sm">
                {slides.map((slide, i) => (
                  <button
                    key={slide.key || i}
                    type="button"
                    aria-label={`Show slide ${i + 1}`}
                    aria-current={i === index ? 'true' : undefined}
                    onClick={() => go(i)}
                    className="lk-focus flex h-6 items-center justify-center rounded-full px-[3px]"
                  >
                    <span
                      className={`relative block h-1.5 overflow-hidden rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-white/45' : 'w-1.5 bg-white/70'}`}
                    >
                      {i === index && !reduced && (
                        <span
                          key={`${index}-${playing}`}
                          className="absolute inset-0 origin-left rounded-full bg-white"
                          style={{
                            animation: playing ? `lk-hero-progress ${seconds}s linear forwards` : 'none',
                            transform: playing ? undefined : 'scaleX(1)',
                            animationPlayState: running ? 'running' : 'paused',
                          }}
                          onAnimationEnd={() => go(index + 1)}
                        />
                      )}
                      {i === index && reduced && <span className="absolute inset-0 rounded-full bg-white" />}
                    </span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
                className="lk-focus flex h-7 w-7 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-sm transition hover:bg-black/40"
              >
                {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------ promo banner */

export const PromoBanners = ({ section }) => {
  const banners = section.data?.banners || [];
  const track = useSectionTracking(section.id, [banners]);
  if (!banners.length) return null;
  const split = section.data?.layout === 'split' && banners.length > 1;
  return (
    <section className="lk-container" {...track}>
      {section.title && <h2 className="mb-3 text-[1.2rem] font-bold text-ink-900 sm:text-h2">{section.title}</h2>}
      <div className={split ? `grid gap-3 sm:gap-4 ${banners.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}` : 'grid gap-3 sm:gap-4'}>
        {banners.map((b) => (
          <BannerSlide key={b.key} slide={b} size={split ? 'split' : 'wide'} className="shadow-card transition-shadow hover:shadow-cardHover" />
        ))}
      </div>
    </section>
  );
};
